<template>
  <v-card outlined class="mb-3 mx-2 px-2" style="page-break-inside: avoid;">
    <v-row>
      <v-col cols="2" class="text-center" align-self="center" style="font-size: 40px">
        <!-- what onfleet actually has for this address: ideally one task holding everyone -->
        <div class="text-caption text-left mb-2 d-print-none" style="overflow-wrap: anywhere">
          <div v-for="task of stop.tasks" v-bind:key="task.shortId">
            <a :href="task.url" target="_blank" rel="noopener" v-if="task.url">{{ task.shortId }}</a>
            <template v-else>{{ task.shortId }}</template>
            <template v-if="task.time">&middot; {{ task.time }}</template>
            &middot; {{ task.driver }}:
            <b>{{ task.name }}</b>
          </div>
          <div v-if="stop.unlinkedTaskIds.length">
            Onfleet task {{ stop.unlinkedTaskIds.join(', ') }}, not linked to any order
          </div>
          <div v-if="stop.notExportedNames.length">
            Not in Onfleet yet: {{ stop.notExportedNames.join(', ') }}
          </div>
        </div>
        <div class="d-print-none">
          <v-btn
            v-if="stop.hasUnexported && !stop.tasks.length"
            small
            class="mb-2 mr-4"
            :loading="exporting"
            @click="$emit('export', stop)"
            color="primary"
            :disabled="shippingGroup!=='local'"
          >
            Export
          </v-btn>
          <!-- half exported: add the customers not on a task yet to the task already at the address -->
          <v-btn
            v-if="stop.hasUnexported && stop.tasks.length === 1"
            small
            class="mb-2 mr-4"
            :loading="exporting"
            @click="$emit('combine', stop)"
            color="warning"
            :disabled="shippingGroup!=='local'"
            :title="`Add ${stop.notExportedNames.join(', ')} to Onfleet task ${stop.tasks[0].shortId}`"
          >
            Combine
          </v-btn>
          <v-btn
            small
            outlined
            class="mb-2 mr-4"
            :loading="exporting"
            @click="$emit('check', stop)"
            v-if="stop.exported"
            color="green"
          >
            Check
          </v-btn>
          <v-btn
            small
            outlined
            class="mb-2 mr-4"
            :loading="exporting"
            @click="$emit('link', stop)"
            v-if="!stop.exported && !stop.onfleetTask"
            :disabled="shippingGroup!=='local'"
            color="primary"
          >
            Link
          </v-btn>

          <v-chip x-small outlined color="green" v-if="stop.exported">
            {{ stop.onfleetIds.join(', ') }}
          </v-chip>

          <v-chip large outlined color="red" v-if="stop.donate === 'all'">
            DONATE ALL
          </v-chip>
          <v-chip large outlined color="red" v-else-if="stop.donate === 'some'">
            DONATE SOME
          </v-chip>
        </div>
        <input class="d-none d-print-block" type="checkbox" style="width: 50px; height: 50px;">
      </v-col>
      <v-col cols="">
        <div>{{ stop.address }}</div>
        <!-- where onfleet is sent instead of the address, when it cannot geocode it -->
        <div v-if="stop.coordinates || stop.geocodeError" class="d-print-none my-1">
          <v-alert
            v-if="stop.geocodeError && !hasCurrentCoordinates && !hasStaleCoordinates"
            type="warning" dense outlined class="mb-1"
          >
            Onfleet could not find this address. Find it on the map, confirm the pin, then export again.
            <div v-if="!readOnly" class="mt-1">
              <v-btn small color="primary" data-cy="find-on-map" @click="mapDialog = true">
                <v-icon left small>mdi-map-marker</v-icon>
                Find on map
              </v-btn>
            </div>
          </v-alert>
          <div v-if="hasCurrentCoordinates" class="text-caption" data-cy="stop-coordinates">
            <b>Onfleet location:</b>
            <a :href="mapsUrl(stop.coordinates)" target="_blank" rel="noopener">
              {{ stop.coordinates.lat }}, {{ stop.coordinates.lng }}
            </a>
            ({{ coordinatesOrigin }}) - used until the address is edited
            <template v-if="!readOnly">
              <v-btn x-small text color="primary" @click="mapDialog = true">change</v-btn>
              <v-btn x-small text color="error" :loading="savingCoordinates" @click="clearCoordinates">
                clear
              </v-btn>
            </template>
          </div>
          <v-alert v-if="hasStaleCoordinates" type="warning" dense outlined class="mb-1" data-cy="stale-coordinates">
            <b>Coordinates no longer valid:</b> the address was changed after they were set
            <span v-if="coordinatesSetFor">(they were for {{ coordinatesSetFor }})</span>, so they are not sent to
            Onfleet. If Onfleet cannot find the new address, find it on the map again.
            <div v-if="!readOnly" class="mt-1">
              <v-btn small color="primary" @click="mapDialog = true">
                <v-icon left small>mdi-map-marker</v-icon>
                Find on map
              </v-btn>
              <v-btn small text color="error" :loading="savingCoordinates" @click="clearCoordinates">clear</v-btn>
            </div>
          </v-alert>
          <div v-if="coordinateError" class="error--text text-caption">{{ coordinateError }}</div>
          <!-- only made when opened: the page has hundreds of cards -->
          <coordinate-confirm-dialog
            v-if="mapDialog"
            v-model="mapDialog"
            :stop="stop"
            @saved="$emit('coordinates', $event)"
          />
        </div>
        <div class="text-caption d-print-none">Deliver between {{ stop.earliest }} and
          {{ stop.latest }} on {{ stop.shipDate }}
        </div>
        <div
          v-if="stop.etaText"
          class="text-caption d-print-none font-weight-bold"
          :class="{ok: 'success--text', warning: 'warning--text', error: 'error--text'}[stop.etaStatus]"
        >
          {{ stop.etaText }}
          <span v-if="stop.etaWindowMiss">(outside delivery window)</span>
        </div>
        <div v-for="customer of stop.customers" v-bind:key="customer.name">
          <div>
            <span class="font-weight-bold d-print">{{ customer.name }}</span>
            <v-btn
              v-if="customer.uid"
              :to="{name:'Customer',params:{id:customer.uid}}"
              x-small outlined
              class="d-print-none">
              edit
            </v-btn>

            <br/>
            <div class="d-print-none caption">
              QRCodes: {{ customer.qrCodes.join(', ') }}
            </div>
          </div>
          <div class="d-print-none text-caption">
            Driver: {{ customer.driver }} &middot;
            Delivery time {{ customer.customerSetTimes ? 'set by customer' : 'using defaults' }}
          </div>
          <div style="font-size: small" v-if="customer.instruction">
            <b>Note:</b> {{ customer.instruction }}
          </div>
          <v-alert type="error" v-for="(error, index) of customer.errors" v-bind:key="index">
            {{ error }}
          </v-alert>
        </div>
      </v-col>
      <!-- right column: how onfleet has the address grouped, and how far the eta misses the window -->
      <v-col cols="3" class="d-print-none">
        <v-alert v-if="stop.stopsInOnfleet > 1" type="warning" dense outlined class="mb-2">
          This address is {{ stop.stopsInOnfleet }} separate stops in Onfleet - it should be one task:
          <b>{{ stop.expectedName }}</b>
        </v-alert>
        <div v-else-if="stop.customerCount > 1" class="text-caption success--text mb-2">
          {{ stop.customerCount }} customers at this address -
          {{ stop.tasks.length ? 'grouped on one Onfleet task' : 'will be exported as one Onfleet task' }}
        </div>
        <div
          v-if="stop.etaWindowMiss"
          class="text-center"
          style="font-size: 28px; line-height: 1.2"
          :class="stop.etaStatus === 'error' ? 'error--text' : 'warning--text'"
        >
          {{ stop.etaWindowMiss }}
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
/**
 * One delivery (all orders at an address, for one driver) on the Shipments page.
 * Everything it shows is worked out up front in the `stop` (see deliveryStopsByDriver in
 * Shipments.vue), so a card only re-renders when its own stop changes - the page has
 * ~700 of them and re-rendering them all on every filter change took seconds.
 */
import api from '@/api';
import {mapsUrl} from '@/utils/address-coordinates';
import {formatAddress} from '@/store/utils';
import CoordinateConfirmDialog from '@/components/CoordinateConfirmDialog';

export default {
  name: 'ShipmentStopCard',
  components: {CoordinateConfirmDialog},
  props: {
    stop: {type: Object, required: true},
    exporting: {type: Boolean, default: false, required: false},
    shippingGroup: {type: String, default: null, required: false},
    readOnly: {type: Boolean, default: false, required: false},
  },
  data() {
    return {
      mapDialog: false,
      savingCoordinates: false,
      coordinateError: '',
    };
  },
  computed: {
    hasCurrentCoordinates() {
      return !!(this.stop.coordinates && this.stop.coordinates.state === 'current');
    },
    hasStaleCoordinates() {
      return !!(this.stop.coordinates && this.stop.coordinates.state === 'stale');
    },
    /** the address out of date coordinates were set for, when they kept a copy of it */
    coordinatesSetFor() {
      const used = this.stop.coordinates && this.stop.coordinates.addressUsed;
      return used ? formatAddress(used, {excludeUnit: true}) : '';
    },
    coordinatesOrigin() {
      const {source, savedBy} = this.stop.coordinates;
      if (source === 'manual') {
        return `entered by ${savedBy || 'someone'}`;
      }
      // google lookups saved by an export (before the map dialog) have no savedBy
      return savedBy ? `found with Google, confirmed by ${savedBy}` : 'looked up with Google';
    },
  },
  methods: {
    mapsUrl,
    /** take the coordinates off the stop's customers; the export goes back to sending the address */
    async clearCoordinates() {
      this.savingCoordinates = true;
      this.coordinateError = '';
      const {uids, coordinateDate: date} = this.stop;
      try {
        await api.post('v2/onfleet/coordinates', {uids, date, clear: true});
        this.$emit('coordinates', {uids, date, coordinates: null});
      } catch (e) {
        const data = e.response && e.response.data;
        this.coordinateError = (data && data.error) || e.message;
      }
      this.savingCoordinates = false;
    },
  },
}
</script>
